/**
 * Documento imprimible de la Declaración de Renta AT 2025
 * Régimen Pro PYME (Art. 14 letra D) N° 3 LIR) — Recuadros N° 17 a 21.
 *
 * El documento se entrega al cliente para su revisión y firma, por lo que
 * está diseñado para ser impreso: sin colores ni fondos. La jerarquía se
 * construye solo con tipografía y líneas, siguiendo la convención contable
 * (línea simple = subtotal, línea gruesa = total, doble línea = resultado
 * final del ejercicio).
 */
const { formatCurrency } = require('../../common/formatUtils');
const { r17: r17Fn, r18: r18Fn, r19: r19Fn, r20: r20Fn, r21: r21Fn } = require('./formulas');

const ANIO_TRIBUTARIO = 2025;
const ANIO_COMERCIAL = ANIO_TRIBUTARIO - 1;

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

const fmt = (val) => {
  const result = formatCurrency(val);
  return result !== '' && result !== null && result !== undefined ? result : '0';
};

const fechaCorta = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
};

const fechaLarga = () => {
  const d = new Date();
  return `${d.getDate()} de ${MESES[d.getMonth()]} de ${d.getFullYear()}`;
};

// -----------------------------------------------------------------------
// Helpers HTML
// -----------------------------------------------------------------------

const fieldRow = (sign, code, label, value, type = '') => {
  const cls = type ? ` row-${type}` : '';
  return `
    <div class="field-row${cls}">
      <span class="sign">${sign}</span>
      <span class="code">${code}</span>
      <span class="label">${label}</span>
      <span class="value">${value}</span>
    </div>`;
};

const subsectionHtml = (title, rows) => `
  <div class="subsection">
    <div class="subsection-title">${title}</div>
    ${rows.join('')}
  </div>`;

const sectionHtml = (numeral, title, subtitle, content, extraClass = '') => `
  <div class="section${extraClass ? ` ${extraClass}` : ''}">
    <div class="section-header">
      <div class="section-title">${numeral ? `${numeral}.&nbsp;&nbsp;` : ''}${title}</div>
      <div class="section-subtitle">${subtitle}</div>
    </div>
    <div class="section-body">${content}</div>
  </div>`;

const metaRow = (label, value) => `
  <div class="meta-row">
    <span class="meta-label">${label}</span>
    <span class="meta-value">${value}</span>
  </div>`;

// -----------------------------------------------------------------------
// R17 – Base Imponible (ingresos y egresos)
// -----------------------------------------------------------------------
const buildR17 = (data, computed) => {
  const ingresos = subsectionHtml('Ingresos', [
    fieldRow('(+)', '1400', 'Ingresos del giro percibidos',                           fmt(r17Fn.ingresosGiroPercibidos(data))),
    fieldRow('(+)', '1817', 'Ingresos devengados en ejercicios anteriores y percibidos', fmt(r17Fn.ingresosDevengadosPercibidos(data))),
    fieldRow('(+)', '1401', 'Rentas de fuente extranjera percibidas',                  fmt(r17Fn.rentasFuenteExtranjera(data))),
    fieldRow('(+)', '1587', 'Ingresos operaciones con empresas relacionadas (14A)',    fmt(r17Fn.ingresosRelacionados14A(data))),
    fieldRow('(+)', '1588', 'Otros ingresos percibidos o devengados',                  fmt(r17Fn.otrosIngresosPercibidos(data))),
    fieldRow('(=)', '1410', 'Total de ingresos anuales',                               fmt(computed.r17?.totalIngresos), 'subtotal'),
  ]);

  const egresos = subsectionHtml('Egresos', [
    fieldRow('(-)', '1409', 'Existencias, insumos y servicios pagados',                fmt(r17Fn.existenciasPagadas(data))),
    fieldRow('(-)', '1818', 'Gastos adeudados en ejercicios anteriores y pagados',     fmt(r17Fn.gastosAdeudadosPagados(data))),
    fieldRow('(-)', '1411', 'Remuneraciones pagadas',                                  fmt(r17Fn.remuneracionesPagadas(data))),
    fieldRow('(-)', '1412', 'Honorarios pagados',                                      fmt(r17Fn.honorariosPagados(data))),
    fieldRow('(-)', '1413', 'Adquisición bienes activo fijo, pagados',                 fmt(r17Fn.activoFijoPagado(data))),
    fieldRow('(-)', '1415', 'Arriendos pagados',                                       fmt(r17Fn.arriendosPagados(data))),
    fieldRow('(-)', '1419', 'Intereses y reajustes pagados',                           fmt(r17Fn.interesesPagados(data))),
    fieldRow('(-)', '1422', 'Partidas art. 21 inc. 1° y 2° LIR pagados',              fmt(r17Fn.partidasArt21(data))),
    fieldRow('(-)', '1424', 'Otros gastos deducibles',                                 fmt(r17Fn.otrosGastos(data))),
    fieldRow('(=)', '1430', 'Total de egresos anuales',                                fmt(computed.r17?.totalEgresos), 'subtotal'),
  ]);

  return sectionHtml(
    'I',
    'Recuadro N° 17 — Base Imponible',
    'Régimen Pro PYME, Art. 14 letra D) N° 3 de la Ley sobre Impuesto a la Renta',
    ingresos + egresos
  );
};

// -----------------------------------------------------------------------
// R18 – Determinación del RAI
// -----------------------------------------------------------------------
const buildR18 = (data, computed) => {
  const rows = [
    fieldRow('(+)', '1703', 'CPTS positivo final (recuadro N° 19)',                   fmt(r18Fn.cptsPositivo(data))),
    fieldRow('(+)', '1704', 'Remesas, retiros o dividendos repartidos, históricos',    fmt(r18Fn.retirosHistoricos(data))),
    fieldRow('(=)', '1720', 'Subtotal',                                                fmt(r18Fn.subtotal(data)), 'subtotal'),
    fieldRow('(-)', '1493', 'Saldo positivo del registro REX antes de imputaciones',  fmt(r18Fn.saldoREXPositivo(data))),
    fieldRow('(-)', '1494', 'Capital aportado, histórico',                             fmt(r18Fn.capitalAportado(data))),
    fieldRow('(=)', '1500', 'Rentas afectas a IGC o IA (RAI) del ejercicio',           fmt(computed.r18?.rai), 'total'),
  ];
  return sectionHtml(
    'II',
    'Recuadro N° 18 — Determinación del RAI',
    'Rentas afectas a Impuesto Global Complementario o Adicional',
    `<div class="subsection">${rows.join('')}</div>`
  );
};

// -----------------------------------------------------------------------
// R19 – CPTS
// -----------------------------------------------------------------------
const buildR19 = (data, computed) => {
  const rows = [
    fieldRow('(+)', '1445', 'CPT o CPTS positivo inicial',                            fmt(r19Fn.cptInicial(data))),
    fieldRow('(+)', '1374', 'Capital aportado empresas que inician actividades en el año comercial', fmt(r19Fn.capitalAportado1374(data))),
    fieldRow('(+)', '1705', 'Base imponible afecta a IDPC del ejercicio',              fmt(r19Fn.biAfecta(data))),
    fieldRow('(+)', '1726', 'Retiros o dividendos percibidos en el ejercicio',         fmt(r19Fn.dividendosPercibidos(data))),
    fieldRow('(-)', '1479', 'Remesas, retiros o dividendos repartidos',                fmt(r19Fn.retiros(data))),
    fieldRow('(-)', '1708', 'Partidas inc. 1° art. 21 LIR',                           fmt(r19Fn.partidasArt21(data))),
    fieldRow('(-)', '1379', 'Crédito IPE contra IF del ejercicio',                    fmt(r19Fn.creditoIPE(data))),
    fieldRow('(+)', '1710', 'Incentivo al ahorro art. 14 letra E)',                   fmt(r19Fn.incentivoAhorro(data))),
    fieldRow('(=)', '1545', 'CPTS positivo final',                                    fmt(computed.r19?.cptsPositivoFinal), 'total'),
  ];
  return sectionHtml(
    'III',
    'Recuadro N° 19 — Capital Propio Tributario Simplificado',
    'Determinación del CPTS al término del ejercicio',
    `<div class="subsection">${rows.join('')}</div>`
  );
};

// -----------------------------------------------------------------------
// R20 – Registro RTRE (tabla RAI / INR / STUT)
// -----------------------------------------------------------------------
const buildR20 = (data) => {
  const rem    = r20Fn.remanente(data);
  const remSig = r20Fn.remanenteSiguiente(data);

  const tableRows = [
    { label: 'Remanente ejercicio anterior (saldo positivo)', code: '1451/1454/1382', values: rem },
    { label: 'Reversos y/o disminuciones (propios)',          code: '1459',           values: { RAI: r20Fn.reverso(data).RAI } },
    { label: 'Aumentos del ejercicio (propios)',              code: '1463',           values: { RAI: r20Fn.aumentoRAI(data).RAI } },
    { label: 'Otros aumentos del ejercicio',                  code: '1470/1387',      values: { REX_INR: r20Fn.aumentoINR(data).REX_INR } },
    { label: 'Retiros imputados a RTRE',                      code: '1475',           values: { RAI: r20Fn.retiros(data) } },
    { label: 'Remanente ejercicio siguiente',                 code: '1484/1487/1391', values: remSig, isTotal: true },
  ];

  const bodyRows = tableRows.map((r) => {
    const cls = r.isTotal ? ' class="row-total"' : '';
    return `<tr${cls}>
      <td class="td-label">${r.label}</td>
      <td class="td-code">${r.code}</td>
      <td>${fmt(r.values.RAI)}</td>
      <td>${fmt(r.values.REX_INR)}</td>
      <td>${fmt(r.values.STUT)}</td>
    </tr>`;
  }).join('');

  const content = `
    <table class="data-table">
      <thead>
        <tr>
          <th class="td-label">Detalle</th>
          <th class="td-code">Código</th>
          <th>RAI</th>
          <th>INR</th>
          <th>STUT</th>
        </tr>
      </thead>
      <tbody>${bodyRows}</tbody>
    </table>`;

  return sectionHtml(
    'IV',
    'Recuadro N° 20 — Registro RTRE y movimiento STUT',
    'Registro Tributario de Rentas Empresariales',
    content
  );
};

// -----------------------------------------------------------------------
// R21 – Registro SAC
// -----------------------------------------------------------------------
const buildR21 = (data) => {
  const rem        = r21Fn.remanente(data);
  const idpcBI     = r21Fn.idpcBI(data);
  const idpcDiv    = r21Fn.idpcDividendo(data);
  const asigRet    = r21Fn.asignadoRetiros(data);
  const gastosR    = r21Fn.gastosRechazados(data);
  const remSig     = r21Fn.remanenteSiguiente(data);

  const sacRows = [
    { label: 'Remanente ejercicio anterior',      noSujetoSinDev: rem.SAC_noSujetoSinDev,    noSujetoConDev: rem.SAC_noSujetoConDev,    sujetoConDev: rem.SAC_sujetoConDev,    ipeSinDev: rem.SAC_IPE_sinDev,       sac2016: rem.SAC_2016_conDev },
    { label: 'IDPC e IPE base imponible',          noSujetoSinDev: idpcBI.SAC_IPE_sinDev,     noSujetoConDev: idpcBI.SAC_noSujetoConDev, sujetoConDev: 0,                       ipeSinDev: idpcBI.SAC_IPE_IF,        sac2016: 0 },
    { label: 'IDPC e IPE dividendos percibidos',  noSujetoSinDev: 0,                          noSujetoConDev: idpcDiv.SAC_noSujetoConDev, sujetoConDev: idpcDiv.SAC_sujetoConDev, ipeSinDev: 0,                       sac2016: idpcDiv.SAC_2016_conDev },
    { label: 'Asignado a retiros',                noSujetoSinDev: asigRet.SAC_noSujetoSinDev, noSujetoConDev: asigRet.SAC_noSujetoConDev, sujetoConDev: 0,                      ipeSinDev: asigRet.SAC_IPE,          sac2016: 0 },
    { label: 'Asignado a gastos rechazados',      noSujetoSinDev: 0,                          noSujetoConDev: gastosR.SAC_noSujetoConDev, sujetoConDev: 0,                       ipeSinDev: 0,                        sac2016: 0 },
    { label: 'Remanente ejercicio siguiente',     noSujetoSinDev: remSig.SAC_noSujetoSinDev,  noSujetoConDev: remSig.SAC_noSujetoConDev, sujetoConDev: remSig.SAC_sujetoConDev, ipeSinDev: remSig.SAC_IPE_sinDev,   sac2016: remSig.SAC_2016_conDev, isTotal: true },
  ];

  const bodyRows = sacRows.map((r) => {
    const cls = r.isTotal ? ' class="row-total"' : '';
    return `<tr${cls}>
      <td class="td-label">${r.label}</td>
      <td>${fmt(r.noSujetoSinDev)}</td>
      <td>${fmt(r.noSujetoConDev)}</td>
      <td>${fmt(r.sujetoConDev)}</td>
      <td>${fmt(r.ipeSinDev)}</td>
      <td>${fmt(r.sac2016)}</td>
    </tr>`;
  }).join('');

  const content = `
    <table class="data-table">
      <thead>
        <tr>
          <th class="td-label">Detalle</th>
          <th>No Suj. S/Dev</th>
          <th>No Suj. C/Dev</th>
          <th>Suj. C/Dev</th>
          <th>IPE</th>
          <th>Cred. 2016</th>
        </tr>
      </thead>
      <tbody>${bodyRows}</tbody>
    </table>`;

  return sectionHtml(
    'V',
    'Recuadro N° 21 — Registro SAC',
    'Saldo Acumulado de Créditos contra impuestos finales',
    content
  );
};

// -----------------------------------------------------------------------
// Resultado del ejercicio: cierre del documento. Va al final, después de
// los recuadros, porque es la cifra que el contribuyente necesita conocer
// y aprobar. Se destaca con doble línea (convención contable), no con color.
// -----------------------------------------------------------------------
const buildResultadoFinal = (data, computed) => {
  const tasaIDPC = data.antecedentes?.tasaIDPC || 0;
  const rows = [
    fieldRow('(+)', '1431', 'Partidas inc. 1° art. 21 LIR (gastos rechazados)',        fmt(r17Fn.gastosRechazadosArt21(data))),
    fieldRow('(=)', '1729', 'Base Imponible antes de incentivo al ahorro',             fmt(computed.r17?.biAntesIncentivo), 'subtotal'),
    fieldRow('(-)', '1432', 'Incentivo al ahorro art. 14 letra E)',                    fmt(r17Fn.incentivoAhorro(data))),
    fieldRow('(=)', '1440', 'Base Imponible afecta a IDPC',                            fmt(computed.r17?.biAfectaIDPC), 'total'),
    fieldRow('(=)', '',     `Impuesto de Primera Categoría determinado (tasa ${tasaIDPC * 100}%)`, fmt(computed.baseImponible?.idpc), 'total'),
    fieldRow('(-)', '36',   'Pagos provisionales mensuales actualizados',              fmt(computed.baseImponible?.ppmActualizado)),
    fieldRow('(=)', '305',  'RESULTADO LIQUIDACIÓN ANUAL IMPUESTO A LA RENTA',         fmt(computed.baseImponible?.resultadoLiquidacionAnual), 'tax-result'),
  ];

  return sectionHtml(
    'VI',
    'Resultado del ejercicio',
    `Determinación final de la Base Imponible, del Impuesto de Primera Categoría y del resultado de la liquidación anual — año comercial ${ANIO_COMERCIAL}`,
    `<div class="subsection">${rows.join('')}</div>`,
    'section-final'
  );
};

// -----------------------------------------------------------------------
// Notas relevantes para la presentación de abril. Se redactan en forma
// condicional porque el saldo definitivo (a pagar o a favor) solo queda
// determinado al presentar la declaración: este documento llega hasta el
// impuesto determinado, sin imputar el reajuste del art. 72 LIR.
// -----------------------------------------------------------------------
const buildNotasDeclaracion = () => `
  <div class="notas-block">
    <div class="block-title">Notas relevantes para la presentación del Formulario 22</div>

    <div class="nota-item">
      <div class="nota-title">1. Reajuste del artículo 72 de la Ley sobre Impuesto a la Renta (código 305)</div>
      <p>Si de la determinación contenida en este documento resulta impuesto a pagar, el monto que deberá enterarse
      efectivamente en arcas fiscales al momento de presentar el Formulario 22 será superior al aquí determinado. Lo
      anterior, por aplicación del artículo 72 de la Ley sobre Impuesto a la Renta, que dispone el reajuste del saldo
      de impuesto adeudado conforme a la variación experimentada por el Índice de Precios al Consumidor entre el último
      día del mes anterior al cierre del ejercicio y el último día del mes anterior a aquel en que legalmente deba
      pagarse el tributo. Dicho reajuste se declara en el código 305 del Formulario 22, se calcula exclusivamente sobre
      el saldo a pagar y su monto definitivo solo se conoce al momento de la presentación de la declaración, durante el
      mes de abril.</p>
      <p>Este reajuste no resulta aplicable cuando la determinación arroja impuesto igual a cero, pérdida tributaria o
      un saldo a favor del contribuyente. En tales casos, las cifras contenidas en el presente documento no
      experimentarán aumento alguno por este concepto.</p>
    </div>

    <div class="nota-item">
      <div class="nota-title">2. Devolución de pagos provisionales mensuales y saldo a favor del contribuyente</div>
      <p>En caso de que los pagos provisionales mensuales enterados durante el ejercicio, y demás créditos imputables,
      excedan el impuesto determinado, se generará un saldo a favor del contribuyente cuya devolución debe solicitarse
      en la misma declaración anual de impuestos a la renta.</p>
      <p>Para que la Tesorería General de la República pueda materializar dicha devolución mediante depósito, el
      contribuyente deberá informar oportunamente a su contador los datos de la cuenta bancaria en que se efectuará el
      abono —de titularidad de la empresa o, en su defecto, de su representante legal—, precisando institución
      bancaria, tipo de cuenta, número de cuenta y RUT del titular. Dicha información deberá ser proporcionada con
      anterioridad a la presentación de la declaración.</p>
      <p>La devolución es depositada por la Tesorería General de la República con posterioridad al mes de abril, en las
      fechas que dicho organismo determine según la modalidad y oportunidad de presentación de la declaración. La
      omisión, inexactitud o desactualización de los datos bancarios podrá impedir o retrasar el pago de la devolución.</p>
    </div>
  </div>`;

// -----------------------------------------------------------------------
// Nota legal
// -----------------------------------------------------------------------
const buildNotaLegal = (company) => {
  const contribuyente = company.name ? `de ${company.name}` : 'del contribuyente';
  return `
  <div class="legal-note">
    <div class="block-title">Nota sobre la preparación y uso de este documento</div>
    <p>La determinación de la base imponible y del Impuesto de Primera Categoría contenida en el presente
    documento, correspondiente al Año Tributario ${ANIO_TRIBUTARIO} (año comercial ${ANIO_COMERCIAL}), ha sido elaborada
    exclusivamente sobre la base de los antecedentes contables, tributarios y documentarios proporcionados
    por el representante legal ${contribuyente}, quien declara y garantiza su integridad, exactitud y veracidad.</p>
    <p>El trabajo realizado consistió en la aplicación de las disposiciones de la Ley sobre Impuesto a la Renta,
    en particular del régimen establecido en su artículo 14 letra D) N° 3, a la información recibida. No constituye
    una auditoría, revisión ni procedimiento de aseguramiento conforme a normas de auditoría generalmente
    aceptadas, por lo que no se emite opinión alguna sobre los estados financieros del contribuyente ni sobre
    la información que sirvió de base para esta determinación.</p>
    <p>Toda modificación, omisión o corrección posterior de los antecedentes entregados podrá alterar las cifras
    aquí determinadas, siendo responsabilidad del contribuyente informar oportunamente tales situaciones.</p>
    <p>Este documento contiene información privada, confidencial y de uso restringido del contribuyente y de su
    representante legal. Su reproducción, distribución o divulgación a terceros, total o parcial, requiere
    autorización previa y por escrito.</p>
  </div>`;
};

// -----------------------------------------------------------------------
// Firmas
// -----------------------------------------------------------------------
const buildFirmas = (company) => {
  const ciudad = company.ciudad || 'Santiago de Chile';
  return `
  <div class="firmas-section">
    <div class="block-title">Aprobación y firma</div>
    <p class="firma-intro">El representante legal declara haber revisado los antecedentes entregados y las cifras
    contenidas en el presente documento, aprobándolas en todas sus partes para efectos de la presentación del
    Formulario 22 correspondiente al Año Tributario ${ANIO_TRIBUTARIO}.</p>
    <p class="firma-lugar">En ${ciudad}, a ${fechaLarga()}.</p>
    <div class="firmas-row">
      <div class="firma-box">
        <div class="firma-space"></div>
        <div class="firma-role">Contador</div>
        <div class="firma-meta">Nombre y RUT</div>
      </div>
      <div class="firma-box">
        <div class="firma-space"></div>
        <div class="firma-role">Representante Legal</div>
        <div class="firma-meta">${company.rep_legal || 'Nombre y RUT'}${company.rut_representante ? ` · RUT ${company.rut_representante}` : ''}</div>
      </div>
    </div>
  </div>`;
};

// -----------------------------------------------------------------------
// CSS: documento en blanco y negro, pensado para impresión y firma.
// -----------------------------------------------------------------------
const CSS = `
  * { box-sizing: border-box; margin: 0; padding: 0; }

  /* El documento se imprime: se fuerza esquema claro para que el render no
     dependa del tema del navegador o del entorno que genera el PDF. */
  html { color-scheme: light; }

  body {
    font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
    font-size: 10.5px;
    line-height: 1.45;
    color: #111;
    background: #fff;
    padding: 4px 2px;
  }

  /* --- Membrete --- */
  .letterhead {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    padding-bottom: 7px;
    border-bottom: 1.5px solid #111;
  }
  .letterhead-entity {
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
  }
  .letterhead-sub {
    font-size: 9px;
    color: #555;
    margin-top: 3px;
    letter-spacing: 0.03em;
  }
  .letterhead-doc {
    text-align: right;
    font-size: 8.5px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: #555;
    white-space: nowrap;
  }

  /* --- Título del documento --- */
  .doc-title-block {
    margin: 26px 0 24px;
    text-align: center;
  }
  .doc-title {
    font-size: 16px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }
  .doc-subtitle {
    font-size: 10px;
    color: #444;
    margin-top: 5px;
    letter-spacing: 0.02em;
  }

  /* --- Bloques con encabezado --- */
  .block-title {
    font-size: 8.5px;
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #111;
    padding-bottom: 5px;
    border-bottom: 0.75px solid #111;
    margin-bottom: 9px;
  }

  /* --- Antecedentes del contribuyente --- */
  .meta-block { margin-bottom: 26px; }
  .meta-grid {
    display: flex;
    flex-wrap: wrap;
  }
  .meta-row {
    display: flex;
    width: 50%;
    padding: 2.5px 16px 2.5px 0;
    font-size: 9.5px;
  }
  .meta-label {
    width: 116px;
    color: #666;
    flex-shrink: 0;
  }
  .meta-value { flex: 1; color: #111; }

  /* --- Secciones --- */
  .section {
    margin-bottom: 22px;
    page-break-inside: avoid;
  }
  .section-header {
    border-bottom: 0.75px solid #111;
    padding-bottom: 5px;
    margin-bottom: 9px;
  }
  .section-title {
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }
  .section-subtitle {
    font-size: 9px;
    color: #666;
    margin-top: 2px;
  }
  .section-body { padding: 0 2px; }

  .subsection { margin-bottom: 13px; }
  .subsection:last-child { margin-bottom: 0; }
  .subsection-title {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #444;
    margin-bottom: 4px;
  }

  /* --- Filas de detalle --- */
  .field-row {
    display: flex;
    align-items: baseline;
    padding: 2.5px 0;
    gap: 8px;
  }
  .sign {
    width: 22px;
    font-size: 9px;
    color: #777;
    text-align: center;
    flex-shrink: 0;
  }
  .code {
    width: 34px;
    font-size: 8.5px;
    color: #777;
    text-align: center;
    flex-shrink: 0;
    font-variant-numeric: tabular-nums;
  }
  .label {
    flex: 1;
    font-size: 10px;
    color: #111;
  }
  .value {
    width: 118px;
    text-align: right;
    font-size: 10px;
    font-variant-numeric: tabular-nums;
    flex-shrink: 0;
  }

  /* Jerarquía por líneas, no por color (convención contable):
     subtotal = línea simple, total = línea gruesa,
     resultado final = línea gruesa arriba y doble línea abajo. */
  .field-row.row-subtotal {
    border-top: 0.5px solid #999;
    margin-top: 3px;
    padding-top: 4px;
  }
  .field-row.row-subtotal .label,
  .field-row.row-subtotal .value { font-weight: 600; }

  .field-row.row-highlight .label,
  .field-row.row-highlight .value { font-weight: 600; }

  .field-row.row-total {
    border-top: 1px solid #111;
    margin-top: 4px;
    padding-top: 5px;
  }
  .field-row.row-total .label,
  .field-row.row-total .value { font-weight: 700; }

  .field-row.row-tax-result {
    border-top: 1px solid #111;
    border-bottom: 3px double #111;
    margin-top: 6px;
    padding: 6px 0;
  }
  .field-row.row-tax-result .label {
    font-weight: 700;
    letter-spacing: 0.02em;
  }
  .field-row.row-tax-result .value {
    font-weight: 700;
    font-size: 12px;
  }

  /* --- Tablas de registros (R20 / R21) --- */
  .data-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 9px;
  }
  .data-table th {
    font-size: 8px;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: #444;
    padding: 5px 6px;
    text-align: right;
    border-bottom: 0.75px solid #111;
    white-space: nowrap;
  }
  .data-table td {
    padding: 4px 6px;
    text-align: right;
    font-variant-numeric: tabular-nums;
    border-bottom: 0.4px solid #ddd;
  }
  .data-table .td-label {
    text-align: left;
    width: 40%;
  }
  .data-table .td-code {
    text-align: center;
    font-size: 8px;
    color: #777;
    white-space: nowrap;
  }
  .data-table tr.row-total td {
    border-top: 1px solid #111;
    border-bottom: none;
    font-weight: 700;
    padding-top: 5px;
  }

  /* --- Resultado del ejercicio (cierre enmarcado, sin relleno) --- */
  .section-final {
    border: 1px solid #111;
    padding: 14px 16px 12px;
    margin-top: 28px;
  }
  .section-final .section-header {
    border-bottom: 0.5px solid #999;
  }

  /* --- Notas de la declaración y nota legal --- */
  .notas-block { margin-top: 30px; }
  .nota-item {
    margin-bottom: 13px;
    page-break-inside: avoid;
  }
  .nota-item:last-child { margin-bottom: 0; }
  .nota-title {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.03em;
    color: #111;
    margin-bottom: 4px;
  }

  .legal-note {
    margin-top: 30px;
    page-break-inside: avoid;
  }

  .notas-block p,
  .legal-note p {
    font-size: 8.5px;
    line-height: 1.6;
    color: #333;
    text-align: justify;
    margin-bottom: 7px;
  }
  .notas-block p:last-child,
  .legal-note p:last-child { margin-bottom: 0; }

  /* --- Firmas --- */
  .firmas-section {
    margin-top: 30px;
    page-break-inside: avoid;
  }
  .firma-intro {
    font-size: 8.5px;
    line-height: 1.6;
    color: #333;
    text-align: justify;
    margin-bottom: 10px;
  }
  .firma-lugar {
    font-size: 9px;
    color: #333;
    margin-bottom: 8px;
  }
  .firmas-row {
    display: flex;
    justify-content: space-between;
    gap: 60px;
    margin-top: 34px;
  }
  .firma-box {
    flex: 1;
    text-align: center;
  }
  .firma-space {
    height: 46px;
    border-bottom: 0.75px solid #111;
    margin-bottom: 6px;
  }
  .firma-role {
    font-size: 9.5px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  .firma-meta {
    font-size: 8.5px;
    color: #666;
    margin-top: 2px;
  }

  @media print {
    body { padding: 0; }
    .section { page-break-inside: avoid; }
  }
`;

// -----------------------------------------------------------------------
// Documento completo
// -----------------------------------------------------------------------
const buildR17_R21_Html = (data, computed, company = {}) => {
  const sectionsHtml = [
    buildR17(data, computed),
    buildR18(data, computed),
    buildR19(data, computed),
    buildR20(data),
    buildR21(data),
    buildResultadoFinal(data, computed),
  ].join('');

  const direccion = [company.address, company.comuna, company.ciudad].filter(Boolean).join(', ');
  const representante = company.rep_legal
    ? `${company.rep_legal}${company.rut_representante ? ` · RUT ${company.rut_representante}` : ''}`
    : '—';

  const antecedentes = `
  <div class="meta-block">
    <div class="block-title">Antecedentes del contribuyente</div>
    <div class="meta-grid">
      ${metaRow('Razón social', company.name || '—')}
      ${metaRow('RUT', company.rut || '—')}
      ${metaRow('Domicilio', direccion || '—')}
      ${metaRow('Representante legal', representante)}
      ${metaRow('Régimen tributario', 'Pro PYME General — Art. 14 D) N° 3 LIR')}
      ${metaRow('Año tributario', `${ANIO_TRIBUTARIO} (comercial ${ANIO_COMERCIAL})`)}
      ${metaRow('Formulario', 'N° 22 — Recuadros N° 17 a 21')}
      ${metaRow('Fecha de emisión', fechaCorta())}
    </div>
  </div>`;

  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8" />
  <title>Declaración de Renta AT ${ANIO_TRIBUTARIO} — ${company.name || 'Contribuyente'}</title>
  <style>${CSS}</style>
</head>
<body>
  <div class="letterhead">
    <div>
      <div class="letterhead-entity">${company.name || 'Contribuyente'}</div>
      <div class="letterhead-sub">${company.rut ? `RUT ${company.rut}` : ''}${direccion ? ` · ${direccion}` : ''}</div>
    </div>
    <div class="letterhead-doc">
      Formulario 22<br />Año Tributario ${ANIO_TRIBUTARIO}
    </div>
  </div>

  <div class="doc-title-block">
    <div class="doc-title">Declaración de Renta</div>
    <div class="doc-subtitle">
      Determinación de la Base Imponible y del Impuesto de Primera Categoría<br />
      Régimen Pro PYME — Art. 14 letra D) N° 3 de la Ley sobre Impuesto a la Renta
    </div>
  </div>

  ${antecedentes}
  ${sectionsHtml}
  ${buildNotasDeclaracion()}
  ${buildNotaLegal(company)}
  ${buildFirmas(company)}
</body>
</html>`;
};

// -----------------------------------------------------------------------
// Exportación vía impresión del navegador (se mantiene por compatibilidad)
// -----------------------------------------------------------------------
const downloadR17_R21_Pdf = (data, computed, company) => {
  const html = buildR17_R21_Html(data, computed, company);

  const win = window.open('', '_blank');
  if (!win) {
    alert('No se pudo abrir la ventana de impresión. Verifique que el bloqueador de ventanas emergentes esté desactivado.');
    return;
  }
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 400);
};

module.exports = { buildR17_R21_Html, downloadR17_R21_Pdf };
